import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Rest from "../../Generated/RestClient";
import {BladeType} from "../Services/azurePortalLinks";
import {DownloadLinkControl, ProgressBarControl, ProgressRingControl, TooltipParagrah} from "../../../../Intercom.ReactUI/Views/index";
import {defaultCIState} from "./BotAppSettings";
import {KuduClient, ArmClient, GithubApi, GithubUser, VsoApi, VsoAccount} from "../../Services/index";
import setupRemoteRepo from "../Services/setupRemoteRepo";
import openTab from "../Services/openTab";
import {parseUrl} from '../../../../Intercom.ReactUI/Utils/Url';
import {AbsCodeCIEnablePanel, AbsCodeCIEnbaleProps, AbsCodeCIEnbaleState} from "./AbsCodeCIEnablePanel";
import {InputDialogBase} from "../../Dialogs/InputDialogBase";
import {MessageBox} from "../../Dialogs/MessageBox";

const ScmTypeToIconNames = {
    bitbucketgit: 'bitbucket',
    bitbuckethg: 'bitbucket',
    dropbox: 'dropbox',
    externalgit: 'external_repo',
    externalhg: 'external_repo',
    github: 'github',
    localgit: 'local_git',
    onedrive: 'one_drive',
    tfs: 'visual_studio',
    vso: 'visual_studio',
    vstsrm: 'visual_studio'
}

export function renderIconForScmType(scmType: string): JSX.Element {
    const iconName = ScmTypeToIconNames[scmType.toLowerCase()];
    if (iconName) {
        return (
            <svg className="scm-icon">
                <use xlinkHref={`Client/Images/symbol-defs.svg#icon-${iconName}`}></use>
            </svg>
        );
    }
    return null;
}

export type RepoProvider = 'github' | 'vso' | 'other';

export interface BotAppCIProps extends React.Props<BotAppContinuousIntegration> {
    openBlade(blade: BladeType): void;
    downloadZip(): JQueryPromise<Blob>;
    onZipDownloaded(blobUrl: string): void;
    toggleTip(tip: JSX.Element, target: HTMLElement);
    bot: Rest.BotService;
    scmType: () => JQueryPromise<string>;
    bladeUrl(blade: BladeType): JQueryPromise<string>;
    botDeploymentLogFound: boolean;
    pendingDownload: boolean;
    blobUrl: string;
    kuduClient: () => JQueryPromise<KuduClient>;
    armClient: () => JQueryPromise<ArmClient>;
    botRsrcId: JQueryPromise<string>;
    hideTitle?: boolean;
    expandoOpenedAlways?: boolean;
    showRepoUrl?: boolean;
    onRepoSetup?: () => void;
}

export interface BotAppCIState {
    repoProvider: RepoProvider;
    repoUserName: string,
    repoToken: string,
    repoUrl?: string,
    hostUrl?: string,
    zipBlobUrl?: string,
    pendingEnabling?: boolean,
    srcChecked?: boolean;
}

export function toggleContinuousIntegrationSettings($config : JQuery) {
    $config.toggleClass('closed open');
    $config.find('span.icon-font').text($config.hasClass('closed') ? '\uE019' : '\uE018');
    if ($config.hasClass('open')) {
        $config[0].scrollIntoView();
    }
}

export class BotAppContinuousIntegration extends React.Component<BotAppCIProps, BotAppCIState> {
    constructor(props: BotAppCIProps) {
        super(props);
        this.state = _.extend({}, defaultCIState);
    }

    private _canSetupRepos = false;
    private  fetchCanSetupRepos(): JQueryPromise<boolean> {
        return this.props.kuduClient()
        .then(kc => {
            return kc.dir('site/wwwroot/PostDeployScripts/');
        })
        .then((files: Rest.VfsNodeInfo[]) => {
            const setupScripts = files.filter(file => file.name == 'setupVsoRemoteRepo.cmd' || file.name == 'setupGithubRemoteRepo.cmd');
            const found = setupScripts.length == 2;
            if (found !== this._canSetupRepos) {
                this._canSetupRepos = found;
                this.forceUpdate();
            }
            return $.Deferred<boolean>().resolve(found);
        });
    }

    private get hasRepoInfo() : boolean {
        return this.hasRepoInfoForVso || this.hasRepoInfoForGithub;
    }

    private get hasRepoInfoForVso(): boolean {
        return this.state.repoProvider === 'vso' && !!this.state.hostUrl && !!this.state.repoToken;
    }

    private get hasRepoInfoForGithub() : boolean {
        return this.state.repoProvider === 'github' && !!this.state.repoToken;
    }

    private scmType: string = null;
    public resolveScmType() {
        this.props.scmType().then(x => {
            this.scmType = x;
            this.forceUpdate();
        });
    }

    private configBladeUrl: string = null;
    private resolveConfigBladeUrl() {
        this.props.bladeUrl(BladeType.continuousIntegration).then(x => {
            this.configBladeUrl = x;
            this.forceUpdate();
        })
    }

    private renderDownloadLink(): JSX.Element {
        let item = <li>Waiting for the bot to finish deploying before download the source code.</li>;
        if (this.props.botDeploymentLogFound) {
            const fileName = `${this.props.bot.Id}.zip`;
            let link = <DownloadLinkControl
                fileName={fileName}
                asButton={true}
                downloadBlob={() => this.props.downloadZip() }
                setBlobUrl={blobUrl => {
                    this.props.onZipDownloaded(blobUrl);
                    this.setState({zipBlobUrl: blobUrl});
                }}
                blobUrl={this.props.blobUrl || this.state.zipBlobUrl}
                pendingDownload={this.props.pendingDownload ? 'Generating zip file' : null}/>
            item = <li>download your source code: <p>{link}</p></li>;
        }
        return item;
    }

    private changeField(fld, val: string) {
        let obj : Pick<BotAppCIState, keyof BotAppCIState> = {} as any; 
        obj[fld] = val;
        if (fld === 'repoProvider') {
            obj.repoToken = null;
            obj.repoUserName = null;
        }
        this.setState(obj);
    }

    private get githubClient() : JQueryPromise<GithubApi> {
        if (this.state.repoProvider === 'github' && this.state.repoToken.length > 0) {
            return $.Deferred<GithubApi>().resolve(new GithubApi(this.state.repoToken));
        }
        return $.Deferred<GithubApi>().reject();
    }

    private get vsoClient(): JQueryPromise<VsoApi> {
        /*
        // vso profile API requires OAuth2.0 auth 
        if (this.state.repoProvider === 'vso' && this.state.repoToken.length > 0) {
            return $.Deferred().resolve(new VsoApi(this.state.repoToken));
        }
        */
        return $.Deferred<VsoApi>().reject();
    }

    private get vsoSiteRootUrl() : string {
        if (this.state.hostUrl) {
            const uri = parseUrl(this.state.hostUrl);
            return uri ? `https://${uri.hostname}` : null;
        }
        return null;
    }

    private setupRepos()  {
        let success = false;
        let dialog = new InputDialogBase<AbsCodeCIEnbaleProps, AbsCodeCIEnbaleState>(AbsCodeCIEnablePanel, {},
            { dialogClass: 'status-panel-dialog' });
        dialog.showDialog()
        .then((state: AbsCodeCIEnbaleState) => {
            this.setState({pendingEnabling: true});
            return $.when<any>(this.props.kuduClient(), this.props.armClient(), this.props.botRsrcId);
        })
        .then((kc, ac, rsrcId) => {
            return setupRemoteRepo(kc, ac, rsrcId, this.state);
        })
        .then(state => {
            this.setState(state);
            success = true;
        })
        .fail((xhr: JQueryXHR, e: any, msg: string)  => {
            let errorMessage: string = null;

            if (xhr.responseJSON) {
                let err = xhr.responseJSON;
                if (err.Message) {
                    errorMessage = err.Message;
                }
            }
            if (msg && !errorMessage) {
                errorMessage = msg;
            }
            errorMessage = errorMessage || xhr.statusText || `Status code: ${xhr.status}`;
            // show error message, trim to the first line
            errorMessage = errorMessage.split(/[\n\r]/g)[0];
            MessageBox.show(errorMessage);
        })
        .always(() => {        
            this.setState({pendingEnabling: false})
            if (success) {
                if (this.props.onRepoSetup) {
                    this.props.onRepoSetup();
                }                    
            }
        });
    }

    private fetchRepo()  {
        $.when<any>(this.props.armClient(), this.props.botRsrcId)
        .then((ac: ArmClient, rsrcId: string) => {
            return ac.getSourceControl(rsrcId);
        })
        .then(sc => {
            if (sc && sc.properties && sc.properties.repoUrl)
            {
                this.setState({repoUrl: sc.properties.repoUrl});
            }
        });
    }

    private renderVsoSetup() : JSX.Element[] {
        return [
            <input type="text" key="vso-hostUrl" placeholder="Url to your Visual Studio Team Services site" value={this.state.hostUrl} 
                onChange={x => this.changeField('hostUrl', x.target['value'])}/>,
            <a key="vso_profile_link" href='https://app.vsaex.visualstudio.com/me' target='_blank'>View Visual Studio Profile</a>,
            /*            
            <input type="text" key="vso-repoUser" placeholder="Secondary user account name" value={this.state.repoUserName} 
                onChange={x => this.changeField('repoUserName', x.target['value'])}/>,
            (this.vsoSiteRootUrl ? <a key="vso_user_link" href={`${this.vsoSiteRootUrl}/_details/security/altcreds`} target='_blank'>Get secondary user account name</a> : null),
            */
            <input type="password" key="vso-repoPat" placeholder="Personal access token with 'Project and team' and 'Code' scope"
                value={this.state.repoToken}
                onChange={x => this.changeField('repoToken', x.target['value'])}/>,
            (this.vsoSiteRootUrl ? <a key="vso_pat_link" href={`${this.vsoSiteRootUrl}/_details/security/tokens`} target='_blank'>Generate personal access token</a> : null)
        ];
    }

    private renderGithubSetup() : JSX.Element[] {
        return [
            <input type="text" key="gh-repoUser" placeholder="user account name" value={this.state.repoUserName} 
                onChange={x => this.changeField('repoUserName', x.target['value'])}/>,
            <input type="password" key="gh-repoPat" placeholder="personal access token with 'repo' scope"
                value={this.state.repoToken}
                onChange={x => this.changeField('repoToken', x.target['value'])}/>,
            <a key="gh-tokenLink" href='https://github.com/settings/tokens' target='_blank'>Generate personal access token</a>
        ];
    }

    private renderCustomSetup() : JSX.Element[] {
        switch (this.state.repoProvider) {
            case 'vso':
                return this.renderVsoSetup();
            case 'github':
                return this.renderGithubSetup();
            case 'other':
                return [this.renderGenericCISetup()];
        }
    }

    private renderSetupReposFromBlank() : JSX.Element {
        const pending = this.state.pendingEnabling;
        return this._canSetupRepos 
            ? 
            <div key="setup-repos" className="setup-repos">
                <p>Select a source control provider</p>
                <select name="repo-type" value={this.state.repoProvider} 
                    onChange={x => {this.changeField('repoProvider', x.target['value'])}}>
                    <option value='vso'>Visual Studio Team Services</option>
                    <option value='github'>GitHub</option>
                    <option value='other'>other</option>
                </select>
                {this.renderCustomSetup()}
                {this.state.repoProvider != 'other' 
                    ? <div className="button-row">
                        <button type="button" className="light-button" 
                            disabled={!this.hasRepoInfo || pending}
                            onClick={e => { this.setupRepos();}}>
                            {pending ? <ProgressRingControl className="downloading-zip-progress"/> : null}
                            {pending ? 'Enabling' : 'Enable'}
                        </button>
                      </div>
                    : null}
                { this.state.repoUrl 
                    ? <a className="repo_Url" target="_blank" href={this.state.repoUrl}>{this.state.repoUrl}</a>
                    : null
                }
            </div>
            :
            <div key="setup-repos" className="setup-repos">
                {this.renderGenericCISetup()}
            </div>;
    }

    private get canOpenConfigBlade() : boolean {
        return (this.state.srcChecked || this.scmType !== 'None') && (this.configBladeUrl !== null || !this.props.expandoOpenedAlways);
    }

    private openCIConfigBlade() {
        if (this.configBladeUrl) {
            openTab(this.configBladeUrl);
        } else {
            this.props.openBlade(BladeType.continuousIntegration) 
        }
    }

    private renderSetupRepos() : JSX.Element {
        return (
            this.scmType === 'None'
            ?
            this.renderSetupReposFromBlank()
            :
            <div key="configure_ci" className="setup-repros">
                <div className="button-row">
                    <button type="button" className="light-button" 
                        disabled={!this.canOpenConfigBlade}
                        onClick={this.openCIConfigBlade.bind(this)}>
                        Configure continuous deployment
                    </button>                    
                </div>
            </div>
        )
    }

    private renderGenericCISetup() : JSX.Element {
        const downloadLink = this.renderDownloadLink();
        return (
            <ol className="bot-ci-list">
                {downloadLink}
                <TooltipParagrah 
                    toggleTip={this.props.toggleTip}
                    tip={<span>Add the files from the zip file into a folder or repository in your source control service. <a href='//aka.ms/bf-abs-ci-step2' target='_blank' key='tip-bf-abs-ci-step2'>Learn more</a></span>}>
                    <li>
                    create a folder/repo for the source files in your preferred service
                    </li>
                </TooltipParagrah>
                <p>
                    <input type='checkbox' id='source_ready' checked={this.state.srcChecked} onChange={x => this.setState({srcChecked: x.target['checked']})}/>
                    <label htmlFor='source_ready'>I completed step 2 and can proceed to step 3</label>
                </p>
                <TooltipParagrah
                    toggleTip={this.props.toggleTip}
                    tip={<span>Configure the source control service to publish to Azure when you check in code. <a href='//aka.ms/bf-abs-ci-step3' target='_blank' key='tip-bf-abs-ci-step3'>Learn more</a></span>}>
                    <li className={this.state.srcChecked ? '' : 'disabled'}>Configure continuous deployment</li>
                </TooltipParagrah>
                <p>
                <button type="button" className="light-button" 
                    disabled={!this.canOpenConfigBlade}
                    onClick={this.openCIConfigBlade.bind(this) }>
                    Configure
                </button>
                </p>
            </ol>
        );
    }

    public componentDidMount() {
        if (this.props.expandoOpenedAlways) {
            this.fetchCanSetupRepos();
        }
        this.resolveScmType();
        this.resolveConfigBladeUrl();
        if (this.props.showRepoUrl) {
            this.fetchRepo();
        }
    }

    public componentDidUpdate(prevProps: BotAppCIProps, prevState: BotAppCIState) {
        if (!prevState.repoToken && this.state.repoToken) {     
            if (this.state.repoProvider === 'github') {
                // get the github user login name
                this.githubClient
                    .then(gc => {
                        return gc.user;
                    })
                    .then((user: GithubUser) => {
                        if (this.state.repoProvider === 'github') {
                            this.setState({repoUserName: user.login});
                        }
                    });                   
            } else if (this.state.repoProvider === 'vso') {
                this.setState({repoUserName: 'name'});
                this.vsoClient
                    .then(vc => {
                        return vc.accounts;
                    })
                    .then(accounts => {
                        if (this.state.repoProvider === 'vso') {
                            this.setState({repoUserName: accounts[0].AccountName});
                        }
                    });
            }
        }
    }

    public render(): JSX.Element {
        const scm = this.scmType;
        let bot = this.props.bot;
        let scmInfo = scm === 'None' || !scm ? null : renderIconForScmType(scm);
        let linkText = scmInfo ? 'Edit' : 'Configure';
        return (<section className="bot-app-setting">
            <div className="header">
                {!this.props.hideTitle ? <h3>Continuous deployment</h3>: null}
                {!this.props.hideTitle 
                    ? <p>Deploy your bot code from GitHub, Visual Studio Team Services, and more.</p>
                    : null
                }
            </div>
            <a className={this.props.expandoOpenedAlways ? "expando opened hidden" : "expando closed"} 
                onClick={_.debounce(e => { 
                    let $config = $(e.currentTarget);
                    this.fetchCanSetupRepos();
                    if (!this.props.expandoOpenedAlways) {
                        toggleContinuousIntegrationSettings($config);
                    };
                }, 100, true) }>{linkText} <span className="icon-font">{'\uE019'}</span>
            </a>
            {scmInfo}
            {this.props.showRepoUrl && this.state.repoUrl ? <p><a target="_blank" href={this.state.repoUrl}>{this.state.repoUrl}</a></p> : null}
            <div className="expandee">
                {this.renderSetupRepos()}
                {this.props.hideTitle ? null :
                    <p><b>Note: </b> After enabling continuous deployment, you will no longer be able to modify the code in Azure.</p>
                }
            </div>
            </section>);
    }
}





// WEBPACK FOOTER //
// ./Client/v4/Views/BotAppContinuousIntegration.tsx